import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Calendar, MapPin, Clock, Swords, ChevronLeft } from 'lucide-react';

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const target = new Date(dateStr + 'T00:00:00');
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((target - now) / 86400000);
};

const NextTournamentWidget = () => {
  const { API, user } = useContext(AuthContext);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user) { setData(null); return; }
    axios.get(`${API}/me/next-tournament`)
      .then((res) => setData(res.data))
      .catch(() => setData(null));
  }, [API, user]);

  if (!user || !data?.tournament) return null;

  const t = data.tournament;
  const days = daysUntil(t.date);
  const countdownLabel = days === 0 ? '🎾 היום!' : days === 1 ? 'מחר!' : `בעוד ${days} ימים`;

  return (
    <div className="max-w-7xl mx-auto px-4 pt-6" dir="rtl">
      <Link to={`/tournament/${t.id}`} data-testid="next-tournament-widget" className="block group">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-l from-slate-900 via-slate-800 to-emerald-950 border border-emerald-500/30 p-5 sm:p-6 shadow-xl transition-all group-hover:shadow-emerald-500/20 group-hover:border-emerald-400/50">
          <div className="absolute -top-12 -left-12 w-48 h-48 rounded-full bg-emerald-500/10 blur-2xl" />
          <div className="flex items-center justify-between gap-4 flex-wrap relative">
            <div className="min-w-0">
              <p className="text-emerald-400 text-xs font-bold tracking-wide mb-1">📅 התחרות הבאה שלך</p>
              <h3 className="text-white text-xl sm:text-2xl font-extrabold truncate">{t.name}</h3>
              <div className="flex items-center gap-4 mt-2 text-slate-300 text-sm flex-wrap">
                <span className="flex items-center gap-1"><Calendar className="w-4 h-4 text-emerald-400" />{new Date(t.date).toLocaleDateString('he-IL', { day: 'numeric', month: 'long' })}</span>
                {t.start_time && <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-emerald-400" />{t.start_time}</span>}
                {t.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-emerald-400" />{t.location}</span>}
              </div>
              {data.next_match?.opponent_name && (
                <div className="mt-2 inline-flex items-center gap-1.5 bg-amber-400/15 text-amber-300 text-sm font-bold px-3 py-1 rounded-full" data-testid="next-opponent">
                  <Swords className="w-4 h-4" />
                  המשחק הבא: מול {data.next_match.opponent_name}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="text-center bg-emerald-500/15 border border-emerald-400/30 rounded-2xl px-5 py-3">
                <div className={`font-black ${days <= 1 ? 'text-2xl text-amber-300 animate-pulse' : 'text-2xl text-emerald-300'}`} data-testid="countdown-label">{countdownLabel}</div>
                {days > 1 && <div className="text-[11px] text-slate-400 font-medium mt-0.5">ספירה לאחור</div>}
              </div>
              <ChevronLeft className="w-6 h-6 text-slate-500 group-hover:text-emerald-400 group-hover:-translate-x-1 transition-all" />
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default NextTournamentWidget;
